// src/pages/Dashboard/Admin/Coaching/CoachingDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useCoachingDashboard } from "./hooks/useCoachingDashboard";

import DashboardCoachingHeader from "./components/DashboardCoachingHeader";
import CoachingTeacherSelectorCard from "./components/CoachingTeacherSelectorCard";
import CoachingKpiCards from "./components/CoachingKpiCards";
import CoachingHoursChartPanel from "./components/CoachingHoursChartPanel";
import CoachingEventsTable from "./components/CoachingEventsTable";
import CoachingSummaryPanel from "./components/CoachingSummaryPanel";
import CoachHoursDrawer from "./components/CoachHoursDrawer";
import EmptyCoachingState from "./components/EmptyCoachingState";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedCoachingPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Heures de coaching
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CoachingDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = useCoachingDashboard();

  if (roleId !== 1) {
    return <UnauthorizedCoachingPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardCoachingHeader onRefresh={dashboard.refreshAll} />

        <CoachingKpiCards
          stats={dashboard.globalStats}
          selectedTeacherStats={dashboard.selectedTeacherStats}
          loading={dashboard.loadingTeachers || dashboard.loadingChart}
          hasSelectedTeacher={!!dashboard.selectedTeacher}
        />

        <CoachingTeacherSelectorCard
          teachers={dashboard.teachers}
          selectedTeacher={dashboard.selectedTeacher}
          selectedTeacherId={dashboard.selectedTeacherId}
          loadingTeachers={dashboard.loadingTeachers}
          loadingEvents={dashboard.loadingEvents}
          onSelectTeacher={dashboard.handleTeacherChange}
          onRefreshTeacher={dashboard.refreshSelectedTeacher}
        />

        <CoachingHoursChartPanel
          data={dashboard.chartRows}
          loading={dashboard.loadingChart}
        />

        {!dashboard.selectedTeacherId ? (
          <EmptyCoachingState />
        ) : (
          <>
            <CoachingEventsTable
              teacher={dashboard.selectedTeacher}
              events={dashboard.events}
              loading={dashboard.loadingEvents}
              savingDeleteId={dashboard.savingDeleteId}
              onEditHours={dashboard.openHoursDrawer}
              onUnregister={dashboard.handleUnregisterCoach}
            />

            <CoachingSummaryPanel
              events={dashboard.events}
              loading={dashboard.loadingEvents}
            />
          </>
        )}
      </div>

      <CoachHoursDrawer
        isOpen={dashboard.hoursDrawerOpen}
        event={dashboard.currentEvent}
        saving={dashboard.savingHours}
        onClose={dashboard.closeHoursDrawer}
        onSave={dashboard.handleSaveCoachHour}
      />

      <ToastContainer />
    </section>
  );
}